import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Stack,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Snackbar,
  DialogContentText,
  Chip,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  InputAdornment,
  Pagination,
  Menu,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Badge,
  Avatar,
  Switch,
  FormGroup,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab,
  Toolbar,
  alpha,
  useTheme
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Download as DownloadIcon,
  MoreVert as MoreIcon,
  Person as PersonIcon,
  School as SchoolIcon,
  Security as SecurityIcon,
  AccessTime as TimeIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  ExpandMore as ExpandMoreIcon,
  Group as GroupIcon,
  Settings as SettingsIcon,
  Refresh as RefreshIcon,
  Block as BlockIcon,
  Check as CheckIcon,
  Clear as ClearIcon,
  Send as SendIcon
} from '@mui/icons-material';
import api from '../api/api';
import { DataGridSkeleton } from '../components/LoadingSkeletons';
import PageHeader from '../components/PageHeader';
import { useAuth } from '../context/AuthContext';

// 1. Update User interface
interface User {
  id: number;
  username: string;
  role: string;
  school_id?: number;
  email: string;
  full_name?: string;
  is_active?: boolean;
  created_at?: string;
  last_login?: string;
  school_name?: string;
  phone_number?: string;
}

// Full list of roles (should match backend)
const ALL_ROLES = [
  'superuser', 'admin', 'principal', 'teacher', 'assistant_teacher', 'head_teacher',
  'counselor', 'librarian', 'accountant', 'secretary', 'parent', 'student', 'user'
];

const ROLE_LABELS: Record<string, string> = {
  superuser: 'System Administrator',
  admin: 'School Administrator',
  principal: 'Principal',
  teacher: 'Teacher',
  assistant_teacher: 'Assistant Teacher',
  head_teacher: 'Head Teacher',
  counselor: 'Counselor',
  librarian: 'Librarian',
  accountant: 'Accountant',
  secretary: 'Secretary',
  parent: 'Parent',
  student: 'Student',
  user: 'User',
};

const ROLE_COLORS: Record<string, string> = {
  superuser: '#d32f2f',
  admin: '#1976d2',
  principal: '#388e3c',
  teacher: '#f57c00',
  assistant_teacher: '#7b1fa2',
  head_teacher: '#c2185b',
  counselor: '#0097a7',
  librarian: '#5d4037',
  accountant: '#455a64',
  secretary: '#6d4c41',
  parent: '#2e7d32',
  student: '#1565c0',
  user: '#757575',
};

const ITEMS_PER_PAGE = 10;

// Add phone number formatting utility function
const formatPhoneNumber = (input: string): string => {
  if (!input) return '';
  
  // Remove all non-digit characters
  let digits = input.replace(/\D/g, '');
  
  // If starts with 0, remove it (e.g., 0712345678 -> 712345678)
  if (digits.startsWith('0')) {
    digits = digits.substring(1);
  }
  
  // If already has country code (254), use as is
  if (digits.startsWith('254')) {
    return `+${digits}`;
  }
  
  // If starts with 7 or 1 (Kenyan mobile), add +254 prefix
  if (digits.startsWith('7') || digits.startsWith('1')) {
    return `+254${digits}`;
  }
  
  // If it's a valid length but doesn't match patterns, assume it's a Kenyan number
  if (digits.length >= 9 && digits.length <= 10) {
    return `+254${digits}`;
  }
  
  // Return as is if it doesn't match any pattern
  return input;
};

// Update phone number validation
const validatePhoneNumber = (phone: string): boolean => {
  if (!phone) return true; // Allow empty
  
  const formatted = formatPhoneNumber(phone);
  const digits = formatted.replace(/\D/g, '');
  
  // Should be 12 digits (+254 + 9 digits)
  return digits.length === 12 && digits.startsWith('254');
};

const Users: React.FC = () => {
  const theme = useTheme();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'add' | 'edit'>('add');
  const [dialogUser, setDialogUser] = useState<User | null>(null);
  // 2. Add phone_number to dialogForm state
  const [dialogForm, setDialogForm] = useState({
    username: '',
    password: '',
    email: '',
    full_name: '',
    role: 'user',
    school_id: '',
    is_active: true,
    phone_number: '',
  });
  const [sendOnboarding, setSendOnboarding] = useState(true);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [dialogError, setDialogError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [bulkMenuAnchor, setBulkMenuAnchor] = useState<null | HTMLElement>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [detailsUser, setDetailsUser] = useState<User | null>(null);
  const [sendingOnboarding, setSendingOnboarding] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'warning' }>({ 
    open: false, message: '', severity: 'success' 
  });
  const [activeTab, setActiveTab] = useState(0);

  // Fetch users
  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await api.get('/users/');
      setUsers(response.data as any[]);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      setSnackbar({ open: true, message: 'Failed to fetch users', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // Filtered and paginated users
  const filteredUsers = useMemo(() => {
    return users.filter(user => {
      const matchesSearch = !search || 
        user.username.toLowerCase().includes(search.toLowerCase()) ||
        user.email.toLowerCase().includes(search.toLowerCase()) ||
        (user.full_name && user.full_name.toLowerCase().includes(search.toLowerCase())) ||
        (user.phone_number && user.phone_number.toLowerCase().includes(search.toLowerCase()));
      
      const matchesRole = !filterRole || user.role === filterRole;
      
      const matchesStatus = filterStatus === 'all' || 
        (filterStatus === 'active' && user.is_active !== false) ||
        (filterStatus === 'inactive' && user.is_active === false);
      
      return matchesSearch && matchesRole && matchesStatus;
    });
  }, [users, search, filterRole, filterStatus]);

  const paginatedUsers = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredUsers.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredUsers, currentPage]);

  const totalPages = Math.ceil(filteredUsers.length / ITEMS_PER_PAGE);

  // Statistics
  const stats = useMemo(() => {
    const total = users.length;
    const active = users.filter(u => u.is_active !== false).length;
    const inactive = total - active;
    const byRole = ALL_ROLES.reduce((acc, role) => {
      acc[role] = users.filter(u => u.role === role).length;
      return acc;
    }, {} as Record<string, number>);
    
    return { total, active, inactive, byRole };
  }, [users]);

  // Permission checks
  const canEditUser = (user: User) => {
    if (currentUser?.role === 'superuser') return true;
    if (currentUser?.role === 'admin') {
      return user.school_id === currentUser.school_id && user.role !== 'superuser';
    }
    return false;
  };

  const canDeleteUser = (user: User) => {
    return currentUser?.role === 'superuser';
  };

  const availableRoles = () => {
    if (currentUser?.role === 'superuser') return ALL_ROLES;
    if (currentUser?.role === 'admin') return ALL_ROLES.filter(r => r !== 'superuser');
    return ['user'];
  };

  // Dialog handlers
  const openAddDialog = () => {
    setDialogMode('add');
    setDialogUser(null);
    setDialogForm({
      username: '',
      password: '',
      email: '',
      full_name: '',
      role: 'user',
      school_id: '',
      is_active: true,
      phone_number: '',
    });
    setSendOnboarding(true);
    setDialogError('');
    setDialogOpen(true);
  };

  const openEditDialog = (user: User) => {
    setDialogMode('edit');
    setDialogUser(user);
    
    // Format phone number for display (remove +254 prefix if present)
    let displayPhone = user.phone_number || '';
    if (displayPhone.startsWith('+254')) {
      displayPhone = displayPhone.substring(4); // Remove +254
    }
    
    setDialogForm({
      username: user.username,
      password: '',
      email: user.email,
      full_name: user.full_name || '',
      role: user.role,
      school_id: user.school_id ? String(user.school_id) : '',
      is_active: user.is_active !== false,
      phone_number: displayPhone,
    });
    setDialogError('');
    setDialogOpen(true);
  };

  // Update handleDialogSave to use the new formatting
  const handleDialogSave = async () => {
    setDialogError('');
    setDialogLoading(true);

    // Validation
    if (!dialogForm.email || !dialogForm.full_name) {
      setDialogError('Email and Full Name are required');
      setDialogLoading(false);
      return;
    }
    if (dialogMode === 'add' && (!dialogForm.username || !dialogForm.password)) {
      setDialogError('Username and Password are required for new users');
      setDialogLoading(false);
      return;
    }
    
    // Phone number validation and formatting
    if (dialogForm.phone_number) {
      if (!validatePhoneNumber(dialogForm.phone_number)) {
        setDialogError('Invalid phone number format. Please enter a valid Kenyan mobile number (e.g., 712345678 or 0712345678)');
        setDialogLoading(false);
        return;
      }
    }

    try {
      const payload: any = {
        email: dialogForm.email,
        full_name: dialogForm.full_name,
        role: dialogForm.role,
        is_active: dialogForm.is_active,
        phone_number: dialogForm.phone_number ? formatPhoneNumber(dialogForm.phone_number) : null,
      };
      if (dialogMode === 'add') {
        payload.username = dialogForm.username;
        payload.password = dialogForm.password;
      } else if (dialogForm.password) {
        payload.password = dialogForm.password;
      }
      if (dialogForm.username !== dialogUser?.username) {
        payload.username = dialogForm.username;
      }
      if (currentUser?.role === 'superuser' && dialogForm.school_id) {
        payload.school_id = Number(dialogForm.school_id);
      } else if (currentUser?.role === 'admin') {
        payload.school_id = currentUser.school_id;
      }
          if (dialogMode === 'add') {
      await api.post('/users', { user: { ...payload, send_onboarding: sendOnboarding } });
      setSnackbar({ open: true, message: 'User created successfully!', severity: 'success' });
    } else {
      await api.patch(`/users/${dialogUser!.id}`, payload);
      setSnackbar({ open: true, message: 'User updated successfully!', severity: 'success' });
    }
      setDialogOpen(false);
      fetchUsers();
    } catch (error: any) {
      setDialogError(error?.response?.data?.detail || 'Operation failed');
      setSnackbar({ open: true, message: 'Operation failed', severity: 'error' });
    } finally {
      setDialogLoading(false);
    }
  };

  // Delete handlers
  const handleDelete = (user: User) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!userToDelete) return;
    
    setDialogLoading(true);
    try {
      await api.delete(`/users/${userToDelete.id}`);
      setSnackbar({ open: true, message: 'User deleted successfully!', severity: 'success' });
      fetchUsers();
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to delete user', severity: 'error' });
    } finally {
      setDialogLoading(false);
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    }
  };

  // Bulk operations
  const handleSelectAll = (checked: boolean) => {
    setSelectedUserIds(checked ? paginatedUsers.map(u => u.id) : []);
  };

  const handleSelectUser = (userId: number) => {
    setSelectedUserIds(prev => 
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const handleBulkOperation = async (operation: 'delete' | 'activate' | 'deactivate' | 'role') => {
    if (selectedUserIds.length === 0) return;

    setDialogLoading(true);
    try {
      if (operation === 'delete') {
        await Promise.all(selectedUserIds.map(id => api.delete(`/users/${id}`)));
        setSnackbar({ open: true, message: `${selectedUserIds.length} users deleted`, severity: 'success' });
      } else if (operation === 'activate' || operation === 'deactivate') {
        await Promise.all(selectedUserIds.map(id => 
          api.patch(`/users/${id}`, { is_active: operation === 'activate' })
        ));
        setSnackbar({ open: true, message: `Users ${operation}d successfully`, severity: 'success' });
      }
      setSelectedUserIds([]);
      fetchUsers();
    } catch (error) {
      setSnackbar({ open: true, message: 'Bulk operation failed', severity: 'error' });
    } finally {
      setDialogLoading(false);
      setBulkMenuAnchor(null);
    }
  };

  // Export functionality
  const exportUsers = () => {
    const csvContent = [
      ['ID', 'Username', 'Email', 'Full Name', 'Role', 'School ID', 'Active', 'Phone Number', 'Created At', 'Last Login'],
      ...filteredUsers.map(user => [
        user.id,
        user.username,
        user.email,
        user.full_name || '',
        ROLE_LABELS[user.role] || user.role,
        user.school_id || '',
        user.is_active !== false ? 'Yes' : 'No',
        user.phone_number || '',
        user.created_at ? new Date(user.created_at).toLocaleString() : '',
        user.last_login ? new Date(user.last_login).toLocaleString() : ''
      ])
    ].map(row => row.map(field => `"${field}"`).join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `users_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    
    setSnackbar({ open: true, message: 'Users exported successfully!', severity: 'success' });
  };

  // View details
  const openDetailsDialog = (user: User) => {
    setDetailsUser(user);
    setDetailsDialogOpen(true);
  };

  // Send onboarding message
  const handleSendOnboarding = async () => {
    if (!detailsUser) return;
    
    setSendingOnboarding(true);
    try {
      const response = await api.post(`/users/${detailsUser.id}/send-onboarding`);
      setSnackbar({ 
        open: true, 
        message: (response.data as any).message || 'Onboarding message sent successfully!', 
        severity: 'success' 
      });
    } catch (error: any) {
      setSnackbar({ 
        open: true, 
        message: error?.response?.data?.detail || 'Failed to send onboarding message', 
        severity: 'error' 
      });
    } finally {
      setSendingOnboarding(false);
    }
  };

  // Permission check
  if (!currentUser || (currentUser.role !== 'superuser' && currentUser.role !== 'admin')) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error" icon={<SecurityIcon />}>
          You do not have permission to access the User Management page.
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box>
        <PageHeader title="User Management" />
        <DataGridSkeleton />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader 
        title="User Management" 
        subtitle={`Manage ${stats.total} users across the system`}
        action={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openAddDialog}
            sx={{ borderRadius: 2 }}
          >
            Add User
          </Button>
        }
      />

      {/* Statistics Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' }, gap: 3, mb: 3 }}>
        <Card sx={{ 
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white'
        }}>
          <CardContent>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="h4" fontWeight="bold">{stats.total}</Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>Total Users</Typography>
              </Box>
              <PersonIcon sx={{ fontSize: 40, opacity: 0.8 }} />
            </Stack>
          </CardContent>
        </Card>
        <Card sx={{ 
          background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
          color: 'white'
        }}>
          <CardContent>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="h4" fontWeight="bold">{stats.active}</Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>Active Users</Typography>
              </Box>
              <ActiveIcon sx={{ fontSize: 40, opacity: 0.8 }} />
            </Stack>
          </CardContent>
        </Card>
        <Card sx={{ 
          background: `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.dark} 100%)`,
          color: 'white'
        }}>
          <CardContent>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="h4" fontWeight="bold">{stats.inactive}</Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>Inactive Users</Typography>
              </Box>
              <InactiveIcon sx={{ fontSize: 40, opacity: 0.8 }} />
            </Stack>
          </CardContent>
        </Card>
        <Card sx={{ 
          background: `linear-gradient(135deg, ${theme.palette.info.main} 0%, ${theme.palette.info.dark} 100%)`,
          color: 'white'
        }}>
          <CardContent>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="h4" fontWeight="bold">{Object.keys(stats.byRole).filter(role => stats.byRole[role] > 0).length}</Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>Active Roles</Typography>
              </Box>
              <GroupIcon sx={{ fontSize: 40, opacity: 0.8 }} />
            </Stack>
          </CardContent>
        </Card>
      </Box>

      {/* Filters and Actions */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr 2fr' }, gap: 2, alignItems: 'center' }}>
            <TextField
              fullWidth
              placeholder="Search users by name, email, or phone..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: search && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearch('')}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={filterRole}
                label="Role"
                onChange={(e) => setFilterRole(e.target.value)}
              >
                <MenuItem value="">All Roles</MenuItem>
                {ALL_ROLES.map(role => (
                  <MenuItem key={role} value={role}>
                    {ROLE_LABELS[role]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={filterStatus}
                label="Status"
                onChange={(e) => setFilterStatus(e.target.value as any)}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              {selectedUserIds.length > 0 && (
                <>
                  <Button
                    variant="outlined"
                    startIcon={<MoreIcon />}
                    onClick={(e) => setBulkMenuAnchor(e.currentTarget)}
                  >
                    Bulk Actions ({selectedUserIds.length})
                  </Button>
                  <Menu
                    anchorEl={bulkMenuAnchor}
                    open={Boolean(bulkMenuAnchor)}
                    onClose={() => setBulkMenuAnchor(null)}
                  >
                    <MenuItem onClick={() => handleBulkOperation('activate')}>
                      <ListItemIcon><CheckIcon /></ListItemIcon>
                      <ListItemText>Activate Selected</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => handleBulkOperation('deactivate')}>
                      <ListItemIcon><BlockIcon /></ListItemIcon>
                      <ListItemText>Deactivate Selected</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => handleBulkOperation('delete')}>
                      <ListItemIcon><DeleteIcon /></ListItemIcon>
                      <ListItemText>Delete Selected</ListItemText>
                    </MenuItem>
                  </Menu>
                </>
              )}
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={exportUsers}
              >
                Export
              </Button>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchUsers}
              >
                Refresh
              </Button>
            </Stack>
          </Box>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.1) }}>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedUserIds.length === paginatedUsers.length && paginatedUsers.length > 0}
                    indeterminate={selectedUserIds.length > 0 && selectedUserIds.length < paginatedUsers.length}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                </TableCell>
                <TableCell><strong>User</strong></TableCell>
                <TableCell><strong>Role</strong></TableCell>
                <TableCell><strong>School</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Last Login</strong></TableCell>
                <TableCell><strong>Phone</strong></TableCell>
                <TableCell align="center"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedUsers.map(user => (
                <TableRow 
                  key={user.id}
                  sx={{ 
                    '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.05) },
                    opacity: user.is_active === false ? 0.6 : 1
                  }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedUserIds.includes(user.id)}
                      onChange={() => handleSelectUser(user.id)}
                    />
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Avatar sx={{ width: 32, height: 32, bgcolor: ROLE_COLORS[user.role] }}>
                        {user.full_name?.charAt(0) || user.username.charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {user.full_name || user.username}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {user.email}
                        </Typography>
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={ROLE_LABELS[user.role] || user.role}
                      size="small"
                      sx={{
                        backgroundColor: alpha(ROLE_COLORS[user.role] || '#757575', 0.1),
                        color: ROLE_COLORS[user.role] || '#757575',
                        fontWeight: 'medium'
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {user.school_id || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={user.is_active !== false ? <ActiveIcon /> : <InactiveIcon />}
                      label={user.is_active !== false ? 'Active' : 'Inactive'}
                      size="small"
                      color={user.is_active !== false ? 'success' : 'default'}
                      variant={user.is_active !== false ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{user.phone_number || '-'}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={1} justifyContent="center">
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => openDetailsDialog(user)}
                          color="primary"
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      {canEditUser(user) && (
                        <Tooltip title="Edit User">
                          <IconButton
                            size="small"
                            onClick={() => openEditDialog(user)}
                            color="primary"
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canDeleteUser(user) && (
                        <Tooltip title="Delete User">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(user)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Pagination */}
        {totalPages > 1 && (
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={(_, page) => setCurrentPage(page)}
              color="primary"
              showFirstButton
              showLastButton
            />
          </Box>
        )}
      </Card>

      {/* Add/Edit User Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            {dialogMode === 'add' ? <AddIcon /> : <EditIcon />}
            <Typography variant="h6">
              {dialogMode === 'add' ? 'Add New User' : 'Edit User'}
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)} sx={{ mb: 2 }}>
            <Tab label="Basic Information" />
            <Tab label="Account Settings" />
          </Tabs>
          
          {activeTab === 0 && (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              <TextField
                fullWidth
                label="Username"
                value={dialogForm.username}
                onChange={(e) => setDialogForm(prev => ({ ...prev, username: e.target.value }))}
                required
                disabled={dialogMode === 'edit' && currentUser?.role !== 'superuser'}
              />
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={dialogForm.email}
                onChange={(e) => setDialogForm(prev => ({ ...prev, email: e.target.value }))}
                required
              />
              <TextField
                fullWidth
                label="Full Name"
                value={dialogForm.full_name}
                onChange={(e) => setDialogForm(prev => ({ ...prev, full_name: e.target.value }))}
                required
              />
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  value={dialogForm.role}
                  label="Role"
                  onChange={(e) => setDialogForm(prev => ({ ...prev, role: e.target.value }))}
                >
                  {availableRoles().map(role => (
                    <MenuItem key={role} value={role}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            backgroundColor: ROLE_COLORS[role]
                          }}
                        />
                        {ROLE_LABELS[role]}
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {currentUser?.role === 'superuser' && (
                <TextField
                  fullWidth
                  label="School ID"
                  value={dialogForm.school_id}
                  onChange={(e) => setDialogForm(prev => ({ ...prev, school_id: e.target.value }))}
                  type="number"
                />
              )}
              <TextField
                fullWidth
                label="Phone Number"
                value={dialogForm.phone_number}
                onChange={(e) => setDialogForm(prev => ({ ...prev, phone_number: e.target.value }))}
                placeholder="e.g. 712345678 or 0712345678"
                helperText="Enter Kenyan mobile number starting with 7 or 1 (e.g., 712345678). Leading 0 will be automatically removed."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography variant="body2" color="text.secondary">+254</Typography>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          )}
          
          {activeTab === 1 && (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              <TextField
                fullWidth
                label={dialogMode === 'add' ? 'Password' : 'New Password (leave blank to keep)'}
                type="password"
                value={dialogForm.password}
                onChange={(e) => setDialogForm(prev => ({ ...prev, password: e.target.value }))}
                required={dialogMode === 'add'}
              />
                          {dialogMode === 'edit' && (
              <FormControlLabel
                control={
                  <Switch
                    checked={dialogForm.is_active}
                    onChange={(e) => setDialogForm(prev => ({ ...prev, is_active: e.target.checked }))}
                  />
                }
                label="Account Active"
              />
            )}
            {dialogMode === 'add' && (
              <Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={sendOnboarding}
                      onChange={(e) => setSendOnboarding(e.target.checked)}
                    />
                  }
                  label="Send Welcome Message"
                />
                <Typography variant="caption" color="text.secondary" sx={{ ml: 4, display: 'block' }}>
                  Sends account details and login instructions via email and WhatsApp (if phone number provided)
                </Typography>
              </Box>
            )}
            </Box>
          )}
          
          {dialogError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {dialogError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleDialogSave}
            disabled={dialogLoading}
            startIcon={dialogLoading ? <CircularProgress size={16} /> : undefined}
          >
            {dialogMode === 'add' ? 'Create User' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete user <strong>"{userToDelete?.username}"</strong>? 
            This action cannot be undone and will permanently remove the user from the system.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmDelete}
            disabled={dialogLoading}
            startIcon={dialogLoading ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            Delete User
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Details Dialog */}
      <Dialog 
        open={detailsDialogOpen} 
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Avatar sx={{ bgcolor: ROLE_COLORS[detailsUser?.role || ''] }}>
              {detailsUser?.full_name?.charAt(0) || detailsUser?.username?.charAt(0)}
            </Avatar>
            <Box>
              <Typography variant="h6">{detailsUser?.full_name || detailsUser?.username}</Typography>
              <Typography variant="body2" color="text.secondary">{detailsUser?.email}</Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            <Box sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}>
              <Typography variant="subtitle2" color="text.secondary">User ID</Typography>
              <Typography variant="body1">{detailsUser?.id}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Username</Typography>
              <Typography variant="body1">{detailsUser?.username}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Role</Typography>
              <Chip
                label={ROLE_LABELS[detailsUser?.role || ''] || detailsUser?.role}
                size="small"
                sx={{
                  backgroundColor: alpha(ROLE_COLORS[detailsUser?.role || ''] || '#757575', 0.1),
                  color: ROLE_COLORS[detailsUser?.role || ''] || '#757575'
                }}
              />
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Status</Typography>
              <Chip
                icon={detailsUser?.is_active !== false ? <ActiveIcon /> : <InactiveIcon />}
                label={detailsUser?.is_active !== false ? 'Active' : 'Inactive'}
                color={detailsUser?.is_active !== false ? 'success' : 'default'}
              />
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">School ID</Typography>
              <Typography variant="body1">{detailsUser?.school_id || '-'}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Phone Number</Typography>
              <Typography variant="body1">{detailsUser?.phone_number || '-'}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Created</Typography>
              <Typography variant="body1">
                {detailsUser?.created_at ? new Date(detailsUser.created_at).toLocaleString() : '-'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Last Login</Typography>
              <Typography variant="body1">
                {detailsUser?.last_login ? new Date(detailsUser.last_login).toLocaleString() : 'Never'}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
          {detailsUser && canEditUser(detailsUser) && (
            <>
              <Tooltip title="Send account details and login instructions via email and WhatsApp">
                <Button
                  variant="outlined"
                  startIcon={sendingOnboarding ? <CircularProgress size={16} /> : <SendIcon />}
                  onClick={handleSendOnboarding}
                  disabled={sendingOnboarding}
                >
                  {sendingOnboarding ? 'Sending...' : 'Resend Welcome Message'}
                </Button>
              </Tooltip>
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={() => {
                  setDetailsDialogOpen(false);
                  openEditDialog(detailsUser);
                }}
              >
                Edit User
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Users; 